import { cx } from "@/utils/cx";
import type { StatusTone } from "@/utils/status";

/**
 * Gymkee 2027 status as text (spec status-as-text 1.13; sheet DS35).
 *
 * An 8 mark and a word, with no ground, no border and no padding of its own, so it sits inside
 * a row without changing its rhythm. The colour is always the semantic *text* colour and it only
 * ever confirms the word; a state with no word is not drawn at all. The vocabulary lives in
 * src/utils/status.ts, so the same Stripe state cannot read one way on the portal and another
 * on the invoice.
 */

export interface StatusTextProps {
  /** The word, already run through t(). */
  word: string;
  tone: StatusTone;
  size?: 13 | 14;
  className?: string;
}

const TONE: Record<StatusTone, { mark: string; word: string }> = {
  neutral: { mark: "var(--gk-text-tertiary)", word: "var(--gk-text-primary)" },
  info: {
    mark: "var(--gk-semantic-info)",
    word: "var(--gk-semantic-info-text)",
  },
  success: {
    mark: "var(--gk-semantic-success)",
    word: "var(--gk-semantic-success-text)",
  },
  warning: {
    mark: "var(--gk-semantic-warning)",
    word: "var(--gk-semantic-warning-text)",
  },
  danger: {
    mark: "var(--gk-semantic-danger)",
    word: "var(--gk-semantic-danger-text)",
  },
};

export function StatusText({
  word,
  tone,
  size = 14,
  className,
}: StatusTextProps) {
  const colours = TONE[tone];
  return (
    <span
      className={cx(
        "gap-gk-2 inline-flex w-fit min-w-0 items-center whitespace-nowrap",
        className,
      )}
      style={{
        fontFamily: "var(--gk-font-text)",
        fontSize: size,
        fontWeight: 500,
      }}
    >
      <span
        aria-hidden="true"
        className="rounded-pill shrink-0"
        style={{
          width: "var(--gk-space-2)",
          height: "var(--gk-space-2)",
          background: colours.mark,
        }}
      />
      <span className="truncate" style={{ color: colours.word }}>
        {word}
      </span>
    </span>
  );
}
